import { useEffect, useMemo } from 'react';

import { Loader } from '@/components/Loader';
import * as S from '@/components/ShoppingBag/ShoppingBag.styles';
import { getPaymentMethodsLogos } from '@/components/ShoppingBag/utils';
import { useApplePayAvailability } from '@/hooks/useApplePayAvailability';
import { useGetPaymentGateways } from '@/hooks/useGetPaymentGateways';
import { filterAvailablePaymentMethods } from '@/lib/util';

interface PaymentsSectionProps {
  billingAddressCountryCode?: string;
  hasWatch?: boolean;
  id?: string;
  isDepositPaid?: boolean;
  isPreOrder?: boolean;
  locale?: string;
  totalPrice?: number;
}

export const PaymentMethodsLogo = ({
  billingAddressCountryCode,
  hasWatch = false,
  id,
  isDepositPaid,
  isPreOrder,
  locale,
  totalPrice,
}: PaymentsSectionProps) => {
  const { fetchPaymentGateways, gatewayConfigs, isLoading, multicardConfig } =
    useGetPaymentGateways(id, totalPrice, billingAddressCountryCode);
  const isApplePayAvailable = useApplePayAvailability();

  useEffect(() => {
    if (fetchPaymentGateways && !isLoading) {
      fetchPaymentGateways(true);
    }
  }, [billingAddressCountryCode]);

  // Derive payment methods based on available configurations
  const paymentMethods = useMemo(() => {
    if (!gatewayConfigs) return [];

    const availablePaymentMethods = filterAvailablePaymentMethods(
      gatewayConfigs.flatMap(({ paymentMethods }) => paymentMethods),
      hasWatch,
      multicardConfig?.minAmount ?? 0,
      multicardConfig?.maxAmount ?? 0,
      isPreOrder,
      isDepositPaid,
      true, // Assuming this parameter is always true
      locale,
      billingAddressCountryCode,
    );

    return getPaymentMethodsLogos(availablePaymentMethods, isApplePayAvailable);
  }, [
    gatewayConfigs,
    hasWatch,
    multicardConfig?.minAmount,
    isPreOrder,
    isDepositPaid,
    locale,
    billingAddressCountryCode,
  ]);

  return (
    <S.PaymentLogos data-test-id='ShoppingBagPaymentLogos'>
      {isLoading ? (
        <Loader />
      ) : (
        paymentMethods?.map((paymentGatewayLogo) => paymentGatewayLogo.img)
      )}
    </S.PaymentLogos>
  );
};
